import { useCallback, useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import ReactFlow, {
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    Controls,
    ReactFlowProvider,
    useReactFlow,
} from "react-flow-renderer";
import DataFlowNode from "./DataFlowNode";

import "./style.scss";

const NODE_TYPES = {
    softwareFlow: DataFlowNode,
};

const GraphCanvas = ({
    graph,
    height = 420,
    className = "",
    showFitView = false,
    showMapLegend = false,
    t,
}) => {
    const { fitView } = useReactFlow();
    const [nodes, setNodes] = useState(graph.nodes);
    const [edges, setEdges] = useState(graph.edges);

    useEffect(() => {
        setNodes(graph.nodes);
        setEdges(graph.edges);
    }, [graph, graph.nodes, graph.edges]);

    const onNodesChange = useCallback((changes) => {
        const positionChanges = changes.filter(
            (change) => change.type !== "dimensions"
        );

        if (!positionChanges.length) {
            return;
        }

        setNodes((current) => applyNodeChanges(positionChanges, current));
    }, []);

    const onEdgesChange = useCallback((changes) => {
        setEdges((current) => applyEdgeChanges(changes, current));
    }, []);

    return (
        <div
            className={`data-flow-graph ${className}`.trim()}
            style={{ height }}
        >
            {showFitView && (
                <button
                    type="button"
                    className="data-flow-graph__fit-view"
                    onClick={() => fitView({ padding: 0.2 })}
                >
                    {t("Fit View")}
                </button>
            )}

            {showMapLegend && (
                <div className="data-flow-graph__map-legend">
                    <div className="data-flow-graph__map-legend-title">
                        {t("Relationships")}
                    </div>
                    <div className="data-flow-graph__map-legend-row">
                        <span className="data-flow-graph__edge-sample" />
                        {t("Data Flow")}
                    </div>
                    <div className="data-flow-graph__map-legend-row">
                        <span className="data-flow-graph__edge-sample data-flow-graph__edge-sample--dashed" />
                        {t("Asset Relationship")}
                    </div>
                    <div className="data-flow-graph__map-legend-title">
                        {t("Node Types")}
                    </div>
                    <div className="data-flow-graph__map-legend-row">
                        <span className="data-flow-graph__swatch data-flow-graph__swatch--software" />
                        {t("Software Asset")}
                    </div>
                    <div className="data-flow-graph__map-legend-row">
                        <span className="data-flow-graph__swatch data-flow-graph__swatch--hardware" />
                        {t("Hardware Asset")}
                    </div>
                    <div className="data-flow-graph__map-legend-row">
                        <span className="data-flow-graph__swatch data-flow-graph__swatch--external" />
                        {t("External Source")}
                    </div>
                </div>
            )}

            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.2}
                nodesDraggable
                nodesConnectable={false}
                elementsSelectable={false}
                zoomOnDoubleClick={false}
            >
                <Background variant="dots" gap={16} size={1} color="#E2E4F0" />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
};

const DataFlowGraph = (props) => {
    if (!props.graph) {
        return null;
    }

    return (
        <ReactFlowProvider>
            <GraphCanvas {...props} />
        </ReactFlowProvider>
    );
};

export default withNamespaces()(DataFlowGraph);
